/**
 * Run-level settings the page is holding that differ from the run it loaded.
 *
 * Lives here, beside `AgentPendingState`, because BOTH eval pages need it and each had its
 * own copy — and they had already drifted on which settings they carried. Two
 * implementations of one question is what produced the `named_slot_ids` defect earlier in
 * this work; the wire format and the diff that fills it belong together.
 *
 * A diff, not a dump: these values are hydrated FROM the loaded run, so reporting them
 * wholesale would tell the agent every untouched run had pending settings changes. `null`
 * means CLEARED, as it does for an override.
 *
 * Each page maps its own state into `now` and `was`; a page that has no picker for a
 * setting simply leaves it undefined on both sides and it never appears in the diff.
 */
export type PendingSettingValues = {
  scenarioId?: number | string | null;
  evaluationCaseId?: number | string | null;
  dcaScenarioId?: number | string | null;
  costSource?: string | null;
  volumeSource?: string | null;
  effectiveDate?: string | null;
  priceDeckId?: number | string | null;
};

/** Wire name and whether the value is an id (coerced) or free text (compared as-is). */
const SETTING_FIELDS: Array<[keyof PendingSettingValues, string, boolean]> = [
  ['scenarioId', 'scenario_id', true],
  ['evaluationCaseId', 'evaluation_case_id', true],
  ['dcaScenarioId', 'dca_scenario_id', true],
  ['costSource', 'cost_source', false],
  ['volumeSource', 'slot_volume_source', false],
  // Both are calculator-material and both were missing: eval_calculator reads
  // `effective_date_override` to set the scenario/config date and `price_deck_href` to
  // choose the pricing path. The server has allowlisted them all along, so every agent
  // read kept reporting the SAVED date and deck after the user moved either picker.
  ['effectiveDate', 'effective_date', false],
  ['priceDeckId', 'price_deck_id', true],
];

/**
 * @param {PendingSettingValues} now - What the page is holding.
 * @param {PendingSettingValues} was - What the loaded run has saved.
 * @return {object} Only the settings that differ, empty when none do.
 */
export const diffPendingSettings = (
  now: PendingSettingValues,
  was: PendingSettingValues
): Record<string, number | string | null> =>
  SETTING_FIELDS.reduce<Record<string, number | string | null>>(
    (acc, [key, wireName, isId]) => {
      const nowValue = now[key];
      const wasValue = was[key];
      // Undefined on BOTH sides means the page has no picker for it — not a change.
      if (nowValue === undefined && wasValue === undefined) return acc;

      const normalise = (v: number | string | null | undefined) => {
        if (v === null || v === undefined) return null;
        return isId ? Number(v) : String(v);
      };
      const a = normalise(nowValue);
      const b = normalise(wasValue);
      if (a !== b) acc[wireName] = a;
      return acc;
    },
    {}
  );
